import { useState, useCallback, useEffect } from 'react';
import { IComponentCss, PageTitle, useFetchData } from '@tap/mui-ui';
import { Grid2, Button, IconButton, TextField, Typography, Tooltip } from '@mui/material';
import { useIssueTable } from './search-page-ui/IssueTable';
import { IssueFilter } from './search-page-ui/IssueFilter';
import { ApiFullUserFilter, ApiUserFilter, userFilterService } from '@tap/data-providers';
import { ICancelDialogResult, useDialog } from '@tap/tap-ui';
import { HeaderToolbarPortal } from '../../components/layout/Header';
import { EditIssueDialog, IssueFormMode } from './dialogs/EditIssueDialog';
import { IssueTable } from './search-page-ui/IssueTable';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteFilterDialog from './dialogs/DeleteFilterDialog';
import CurrentFiltersBlock from './list-page-ui/CurrentFiltersBlock';
import { useDocuemtnSchema } from '@tap/doc-ui';
import UserFilterMenuItem, { FilterMenuItem } from './list-page-ui/UserFilterMenuItem';
import { useGridApiRef, GridFilterModel } from '@mui/x-data-grid-pro';

/*
*   Список задач
*/

type IssueListPageProps = {
    issueCodes: string[],
}

const css: IComponentCss = {
    wrap: {
        marginTop: '-25px',
        marginLeft: '-12px',
        width: 'calc(100% + 10px)',
        height: 'calc(100vh - 90px)',
        position: 'relative',
        flexGrow: 1,
        display: 'flex',
    },
    filtersWrap: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '250px',
        height: 'calc(100% + 28px)',
        borderRight: '1px solid #E0E0E0',
        overflow: 'auto',
        padding: '0px 10px 16px 0px',
    },
    filters: {
        paddingTop: '62px',
    },
    tasks: {
        width: '100%',
        padding: '25px 0px 0px 265px',
    },
    filterListTitle: {
        padding: '18px 16px 19px 16px',
        fontWeight: 700,
        fontSize: '12px',
        width: '100%',
    },
    filterListNoRows: {
        padding: '0 0 0 16px',
    },
    addQueueButton: {
        width: '100%',
        justifyContent: 'start',
        paddingLeft: '16px',
        svg: {
            marginTop: '-2px',
        },
    },
    closeOrEditIcon: {
        position: 'absolute',
        top: '6px',
        right: '0px',
    },
    title: {
        mb: 0,
        flexWrap: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    tasksToolbar: {
        marginBottom: '5px',
        position: 'relative',
        paddingRight: '45px',
    },
    breakLine: {
        borderBottom: '1px solid #E0E0E0',
        height: '1px',
        margin: '15px 0px 20px 0px',
    },
    saveOrAddButton: {
        marginTop: '10px',
    },
    deleteFilterButton: {
        marginTop: '10px',
    },
    filterInfoMode: {
        margin: '18px 0px 10px',
        padding: '10px',
        borderRadius: '4px',
        width: '100%',
    },
}

const defaultFilterValue = {
    statusCodes: [],
    assigneeIds: [],
};

const filterProviderProps = {
    useQueryParams: true,
    defaultValue: defaultFilterValue,
};

enum Mode {
    View = 'view',
    Edit = 'edit',
    Add = 'add',
}

const newFilterProps = {
    name: 'Новый фильтр',
    description: '',
    settings: { filterModel: { items: [] } },
};

// TODO: Привести все записи на сервере к новому формату
const transformFilterList = (filterList: ApiFullUserFilter[]) => {
    if (filterList?.[0].settings.filterModel.items?.[0].field)
        return filterList;

    const transformedFilterList = filterList;
    filterList.forEach((filter, idx) => {
        filter.settings.filterModel.items.forEach((item, index) => {
            transformedFilterList[idx].settings.filterModel.items[index] = {
                id: item.id,
                value: item.value,
                operator: item.operator ?? (item as any).operatorValue, // TODO: Update values in DB
                field: item.field ?? (item as any).columnField, // TODO: Update values in DB
            }
        })
    })
    return transformedFilterList;
}

export const IssueListPage = IssueFilter.withProvider(filterProviderProps, ({ issueCodes }: IssueListPageProps) => {
    const xGridApiRef = useGridApiRef();
    const groupCode = issueCodes[0];// TODO: Fix It
    const documentSchema = useDocuemtnSchema(groupCode);

    const [mode, setMode] = useState<Mode>(Mode.View);
    const [currentFilter, setCurrentFilter] = useState<ApiUserFilter>();
    const [editFilter, setEditFilter] = useState<ApiUserFilter>({ ...newFilterProps, groupCode });
    const [columns, rows, rawDataState] = useIssueTable(issueCodes);
    const [state, setState] = useState<FilterMenuItem[]>([]);

    const handleCloseDialog = ({ isCancel }: ICancelDialogResult) => {
        if (!isCancel) {
            rawDataState.fetchData();
        }
    };

    const EditIssueReadyDialog = useDialog(EditIssueDialog, handleCloseDialog);
    const DeleteFilterReadyDialog = useDialog(DeleteFilterDialog, handleCloseDialog);

    const [userFilterListState, userFilterList] = useFetchData(() => {
        return userFilterService.getUserFilterList(groupCode).then((filterList) => {
            const newFilterList = transformFilterList(filterList);
            setCurrentFilter((cf) => cf || newFilterList[0]);
            return newFilterList;
        });
    }, [groupCode]);

    const handleCreateNewFilterClick = () => {
        setEditFilter({ ...newFilterProps, groupCode });
        setMode(Mode.Add);
    }

    const handleSaveFilterClick = async () => {
        if (editFilter.id) {
            await userFilterService.updateUserFilter(editFilter.id, editFilter);
        } else {
            editFilter.id = await userFilterService.createUserFilter(editFilter);
        }

        userFilterListState.fetchData();
        setCurrentFilter({ ...editFilter });
        setMode(Mode.View);
    }

    const handleCancelEditMode = () => {
        setMode(Mode.View);
    }

    const handleDeleteUserFilter = () => {
        const deletedId = editFilter.id as number;
        userFilterService.deleteUserFilter(deletedId)
            .then(() => {
                userFilterListState.setData((val) => {
                    const newList = (val || []).filter(f => f.id !== deletedId);
                    setCurrentFilter(newList[0]);
                    return newList;
                });
                setMode(Mode.View);
            }).catch((err) => {
                setMode(Mode.Edit);
                throw err;
            });
    }

    const handleChangeCurrentFilter = (filter: ApiUserFilter) => {
        setCurrentFilter(filter);
    }

    const handleEditClick = () => {
        if (!currentFilter)
            return;

        setEditFilter(() => ({ ...currentFilter }));
        setMode(Mode.Edit);
    }

    const timeTrackingValues = {
        estimateMin: documentSchema.settings.sla.find(m => m.code === 'responseTime')?.normative,
        leftMin: documentSchema.settings.sla.find(m => m.code === 'responseTime')?.normative,
    }

    const getFilteredRowsCount = useCallback((filterModel: GridFilterModel) => {
        if (!xGridApiRef?.current?.getAllRowIds || !xGridApiRef.current?.getFilterState)
            return 0;

        const rowIds = xGridApiRef?.current?.getAllRowIds();
        const filterState = xGridApiRef.current?.getFilterState(filterModel);

        if (!rowIds || !filterState) {
            return 0;
        }

        const { filteredRowsLookup } = filterState;
        const count = Object.keys(filteredRowsLookup).filter(k => filteredRowsLookup[k]).length;

        return count;
    }, [xGridApiRef, xGridApiRef?.current?.getAllRowIds, xGridApiRef.current?.getFilterState]);

    useEffect(() => {
        if (rows?.length === 0) {
            return;
        }

        const result: FilterMenuItem[] = userFilterList?.map((item) => ({ ...item, count: getFilteredRowsCount(item.settings.filterModel) })) ?? [];
        setState(result);
    }, [xGridApiRef, rows, getFilteredRowsCount]);

    return (
        <>
            <HeaderToolbarPortal>
                <Button
                    variant='contained'
                    disableElevation
                    onClick={() => EditIssueReadyDialog.open({ mode: IssueFormMode.Create, documentCode: groupCode, timeTrackingValues })}
                    size='small'
                    sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                >
                    Создать заявку
                </Button>

                <EditIssueReadyDialog.Component />
            </HeaderToolbarPortal>

            {/* <ReactRouterPrompt when={(mode === Mode.Edit || mode === Mode.Add)}>
                {({ isActive, onCancel, onConfirm }: any) =>
                    <ConfirmExitFromEditingDialog
                        isOpen={isActive}
                        onCancel={onCancel}
                        onConfirm={onConfirm}
                    />
                }
            </ReactRouterPrompt> */}

            <Grid2 container sx={css.wrap}>
                <Grid2
                    sx={css.filtersWrap}
                    container
                    direction="column"
                    justifyContent="space-between"
                    wrap='nowrap'
                >
                    <Grid2
                        container
                        sx={css.filters}
                    >

                        {mode === Mode.View && (
                            <>
                                <Typography sx={css.filterListTitle}>Фильтры</Typography>

                                {!userFilterList?.length && (
                                    <Typography sx={css.filterListNoRows}>Не заданы</Typography>
                                )}

                                {!!state?.length &&
                                    state.map((f: FilterMenuItem) =>
                                        <UserFilterMenuItem
                                            key={f.id}
                                            filter={f}
                                            currentFilter={currentFilter}
                                            handleChangeCurrentFilter={handleChangeCurrentFilter}
                                        />,
                                    )
                                }

                            </>
                        )}



                        {(mode === Mode.Edit || mode === Mode.Add) &&
                            <Typography
                                sx={{
                                    ...css.filterInfoMode,
                                    backgroundColor: mode === Mode.Edit ? 'cornsilk' : '#eef6ff',
                                }}
                            >
                                {mode === Mode.Edit
                                    ? 'Редактирование фильтра'
                                    : 'Добавление нового фильтра'
                                }
                            </Typography>
                        }

                        {(mode === Mode.Edit || mode === Mode.Add) && editFilter &&
                            <CurrentFiltersBlock
                                userFilter={editFilter}
                                columns={columns}
                            />
                        }
                    </Grid2>

                    {mode === Mode.View && (
                        <Grid2 container>
                            <Button
                                onClick={handleCreateNewFilterClick}
                                variant="text"
                                startIcon={<AddIcon />}
                                sx={css.addQueueButton}>
                                Новый фильтр
                            </Button>
                        </Grid2>)
                    }

                    {(mode === Mode.Edit || mode === Mode.Add) && editFilter && (
                        <Grid2 container>
                            {mode === Mode.Edit &&
                                <Button
                                    onClick={() => DeleteFilterReadyDialog.open({ handleDeleteUserFilter, userFilter: editFilter })}
                                    variant="text"
                                    size='small'
                                    color='inherit'
                                    fullWidth
                                    disableElevation
                                    sx={css.deleteFilterButton}
                                    startIcon={<DeleteOutlineOutlinedIcon />}
                                >
                                    Удалить фильтр
                                </Button>
                            }

                            <Grid2 container sx={css.breakLine}></Grid2>

                            <Button
                                onClick={handleSaveFilterClick}
                                variant="contained"
                                color="primary"
                                fullWidth
                                disableElevation
                                sx={css.saveOrAddButton}
                            >
                                {mode === Mode.Edit ? 'Сохранить' : 'Добавить'}
                            </Button>
                        </Grid2>
                    )}
                </Grid2>

                <Grid2 sx={css.tasks}>
                    <Grid2
                        container
                        sx={css.tasksToolbar}
                        wrap='nowrap'
                    >
                        {mode === Mode.View && (
                            <Tooltip title={currentFilter?.name || 'Заявки'}>
                                <div style={{ overflow: 'hidden' }}>
                                    <PageTitle sx={css.title}>
                                        {currentFilter?.name || 'Заявки'}
                                    </PageTitle>
                                </div>
                            </Tooltip>

                        )}

                        {(mode === Mode.Edit || mode === Mode.Add) && editFilter &&
                            <TextField
                                autoFocus
                                fullWidth
                                variant='standard'
                                sx={{
                                    'input.MuiInputBase-input': {
                                        paddingLeft: '2px',
                                    },
                                }}
                                InputProps={{
                                    style: {
                                        height: '45px',
                                        fontSize: '34px',
                                        marginTop: '-2px',
                                        marginLeft: '-2px',
                                    },
                                }}
                                value={editFilter.name}
                                onChange={event => setEditFilter({ ...editFilter, name: event.target.value })}
                            />
                        }

                        {mode === Mode.View && currentFilter &&
                            <IconButton
                                onClick={handleEditClick}
                                size='small'
                                sx={css.closeOrEditIcon}
                            >
                                <EditOutlinedIcon />
                            </IconButton>
                        }

                        {(mode === Mode.Edit || mode === Mode.Add) &&
                            <IconButton
                                onClick={handleCancelEditMode}
                                size='small'
                                sx={css.closeOrEditIcon}
                            >
                                <CloseOutlinedIcon />
                            </IconButton>
                        }


                    </Grid2>

                    {mode === Mode.View && !userFilterList?.length && (
                        <Typography variant='subtitle1'>Для начала работы с заявками добавьте новый фильтр</Typography>
                    )}


                    {(mode !== Mode.View || !!userFilterList?.length) && (

                        <Grid2
                            container
                            sx={{
                                marginTop: (mode === Mode.Edit || mode === Mode.Add) ? '13px' : '51px',
                            }}
                        >
                            <IssueTable
                                key={mode}
                                columns={columns}
                                rows={rows}
                                xGridApiRef={xGridApiRef}
                                rawDataState={rawDataState}
                                issueCodes={issueCodes}
                                showFilter={(mode === Mode.Edit || mode === Mode.Add)}
                                filterModel={mode === Mode.View ? currentFilter?.settings.filterModel : editFilter.settings.filterModel}
                                onFilterModelChange={(newFilterModel) => {
                                    setEditFilter({
                                        ...editFilter,
                                        settings: {
                                            ...editFilter.settings,
                                            filterModel: newFilterModel,
                                        },
                                    })
                                }}
                            />
                        </Grid2>
                    )}
                </Grid2>

            </Grid2>

            <DeleteFilterReadyDialog.Component />
        </>
    );
});
